import { Inject, Injectable, Injector, isDevMode } from "@angular/core";
import { BehaviorSubject, EMPTY, Observable, of, Subject } from "rxjs";
import { catchError, map, mapTo, pluck, switchMap, take, tap } from "rxjs/operators";
import { SocketMessagesService } from "../core/socket/socket.service";
import { TokenStorageService } from "./token-storage.service";
import { type IAuthResponse, UserInfoStorageService } from "./user-info-storage.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { Router } from "@angular/router";
import { ProgressService } from "./progress.service";
import { MessageError } from "../message-error";
import { CustomErrorHandlerService } from "./custom-error-handler.service";
import { IBookingDefaults, IRegion, IUser } from "../main/settings/settings.service";
import { NotificationsService } from "./notifications/notifications.service";
import moment from "moment";
import { CommonService } from "../main/common.service";
import { IBookableEntity, IBillingEntity, IBooking, IOrganization } from "../types";
import { CapabilitiesService } from "../main/capabilities.service";
import { getBookingTypeTile } from "../main/utils";
import { serviceTypeDisplayLabel } from '../constants/service-type-labels';
import orderBy from "lodash/orderBy";

const domain_urls: { [key: string]: string } = environment?.aiLiveDomainUrls;

export class LoginError extends Error {}

export interface ILoginResponse extends IAuthResponse {
  bookeng_app_token: string;
}

export interface ISignInResponse {
  accessToken: string;
  changePasswordRequired: boolean;
  idToken: string;
  refreshToken: string;
  statusMessage: string;
}

export type TAuthMethod = "sso" | "password";

@Injectable()
export class AuthService {
  public org_list!: IOrganization[];
  public be_list!: IBillingEntity[];
  public org_srv_type_list!: { [key: string]: number[] };
  public service_types_list: any = [];
  public summary_option_list!: any;
  public booking_creation_types_list!: any;
  public cached_billing_entity!: IBillingEntity;
  public kb_data$$: any = new BehaviorSubject([]);
  public bookableEntities!: any;
  public curr_billing_entity_id!: number;
  public curr_org_id!: number;
  public current_org$$ = new BehaviorSubject(0);
  public curr_billing_entity_id$$ = new BehaviorSubject(0);
  public authorizationMethod$$: BehaviorSubject<TAuthMethod> = new BehaviorSubject<TAuthMethod>("sso");
  private logged_in_user!: IAuthResponse;

  private authStatusChange$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoggedIn$: Observable<boolean> = this.authStatusChange$$.asObservable().pipe(
    tap((r) => {
      if (isDevMode()) {
        console.debug(r, "<- authorization status --");
      }
    }),
  );

  private logout$$: Subject<Partial<IUser>> = new Subject<Partial<IUser>>();
  private cache_user!: Partial<IUser>;
  public logout$ = this.logout$$.asObservable();

  /** After reLogin / login, ignore spurious "App token not valid" from requests that raced the session refresh. */
  private postReloginGraceUntilMs = 0;
  private readonly POST_RELOGIN_GRACE_MS = 15000;

  public markPostReloginGracePeriod(): void {
    this.postReloginGraceUntilMs = Date.now() + this.POST_RELOGIN_GRACE_MS;
  }

  public isWithinPostReloginGracePeriod(): boolean {
    return Date.now() < this.postReloginGraceUntilMs;
  }

  private route = "bookings";
  public url = "";
  public listOfUsers = "";

  public regions: IRegion[] = [];

  injector: Injector;
  // @Inject(Injector) injector: Injector,
  // get messages() {
  //     return this.injector.get(SocketMessagesService)
  // }

  // private redirectUser: RedirectUserService,
  constructor(
    @Inject(Injector) injector: Injector,
    private token: TokenStorageService,
    private user: UserInfoStorageService,
    private error: CustomErrorHandlerService,
    private http: HttpClient,
    private router: Router,
    private progress: ProgressService,
    private notification: NotificationsService,
  ) {
    this.injector = injector;
    this.logout$
      .pipe(
        switchMap((cache_user) => {
          setTimeout(() => {
            this.cache_user = undefined as unknown as Partial<IUser>;
          }, 1000);
          return this.messages.request$("logout", {
            ...cache_user,
            curr_billing_entity_id: this.curr_billing_entity_id,
            curr_org_id: this.curr_org_id,
          });
        }),
        tap(() => {
          this.authStatusChange$$.next(false);
        }),
      )
      .subscribe();
  }

  get messages() {
    return this.injector.get(SocketMessagesService);
  }

  get common() {
    return this.injector.get(CommonService);
  }

  get capabilities() {
    return this.injector.get(CapabilitiesService);
  }

  public getGoogleAuthUrl$(): Observable<string> {
    return this.http.get(environment.apiServer + "/v1/get-google-login-url").pipe(
      map((response: unknown) => {
        return (response as { url: string }).url;
      }),
    );
  }

  public login$(user_id: string, password: string): Observable<unknown> {
    this.progress.start();
    user_id = user_id.toLowerCase();
    // @ts-ignore
    return this.messages.request$<ILoginResponse>("login", { user_id, password }).pipe(
      this.authorize.bind(this),
      catchError((err) => {
        this.error.handleError(err, "@auth.service1");
        this.progress.stop();
        return EMPTY;
      }),
      tap(() => {
        this.progress.stop();
      }),
    );
  }

  public hasAppToken() {
    return this.token.has();
  }

  public getUserInfo(): IAuthResponse {
    const user = this.user.get() || this.logged_in_user;
    return {
      ...user,
      curr_billing_entity_id: this.curr_billing_entity_id || user?.billing_entity_id,
      curr_org_id: this.curr_org_id || user?.org_id,
    } as IAuthResponse;
  }

  public getAiLiveCaptionUrlParams() {
    let book_def = this.getBookingDefaults();
    const ai_live_caption_setting = book_def.ai_live_caption_setting;

    return (
      "&" +
      Object.entries(ai_live_caption_setting)
        .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
        .join("&")
    );
  }

  public getUserBillingEntityId(): number {
    if (this.getUserInfo()) {
      return this.getUserInfo().billing_entity_id;
    }
    return 0;
  }

  /**
   * Billing entity for portal booking payloads when the user set a portal default account
   * (`booking_defaults.account_default`): use it when it parses as a number; otherwise session
   * `curr_billing_entity_id` or the user's `billing_entity_id`.
   */
  public resolveBookingDefaultsBillingEntityId(): number {
    const user = this.getUserInfo();
    const fallback = (user?.curr_billing_entity_id ?? user?.billing_entity_id) as number;
    const acct = this.getBookingDefaults()?.account_default;
    if (acct != null && `${acct}` !== "") {
      const n = +acct;
      if (!Number.isNaN(n)) {
        return n;
      }
    }
    return fallback;
  }

  /** Active session billing entity after login: portal default when set, else server user billing_entity_id. */
  private resolveLoginBillingEntityId(billing_entity_id: number, booking_defaults?: IBookingDefaults | null): number {
    const acct = booking_defaults?.account_default;
    return acct != null ? acct : billing_entity_id;
  }

  /** Org that owns the billing entity (e.g. super-admin portal default in another org). */
  private resolveOrgIdForBillingEntityFromBookable(
    bookable: IBookableEntity[],
    billingEntityId: number,
    fallbackOrgId: number,
  ): number {
    for (const o of bookable || []) {
      if (o.billing_entities?.some((be) => be.billing_entity_id === billingEntityId)) {
        return o.org_id;
      }
    }
    return fallbackOrgId;
  }

  /**
   * After saving profile (portal `account_default`), align session org/BE, API token, cache, and navbar
   * with the chosen billing entity (including another org for super-admins).
   */
  public syncSessionToAccountDefaultBillingEntity$(billingEntityId: number): Observable<void> {
    const userInfo = this.getUserInfo();
    const fallbackOrgId = this.curr_org_id || userInfo.org_id;
    // @ts-ignore
    return this.messages.request$<{ bookable_entities: IBookableEntity[] }>("getBookableEntities").pipe(
      take(1),
      catchError(() => of({ bookable_entities: [] as IBookableEntity[] })),
      tap(({ bookable_entities }) => {
        const effectiveOrgId = this.resolveOrgIdForBillingEntityFromBookable(
          bookable_entities,
          billingEntityId,
          fallbackOrgId,
        );
        this.current_org$$.next(effectiveOrgId);
        this.curr_org_id = effectiveOrgId;
        this.curr_billing_entity_id = billingEntityId;
        this.curr_billing_entity_id$$.next(billingEntityId);
        this.getKbRecords(effectiveOrgId);
        this.setCachedBillingEntity(effectiveOrgId, billingEntityId);
        this.capabilities.changeOrg(effectiveOrgId);
        this.capabilities.changeBE(billingEntityId);
        this.capabilities.reloadCapabilities();
      }),
      mapTo(void 0 as unknown as void),
    );
  }

  public getUserOrgId(): number {
    if (this.getUserInfo()) {
      return this.getUserInfo().org_id;
    }
    return 0;
  }

  public isSysAdmin(): boolean {
    return this.getUserInfo().sys_admin;
  }

  public getUserTimeZone(): string {
    return this.getUserInfo().time_zone;
  }

  public getBookingTimezone(b: IBooking): string {
    return b.booking_time_zone || this.getUserTimeZone();
  }

  public getApiToken$(org_id: number, be_id: number, user_id: number, type: string) {
    const { sys_admin, developer } = this.getUserInfo();
    if (sys_admin || developer) {
      return this.messages.request$("getApiToken", { org_id, be_id, user_id, type });
    }

    if (!org_id) {
      this.notification.error("Operation invalid!");
      return EMPTY;
    }

    // istanbul ignore next -- unreachable: only reached when sys_admin and developer are both
    // falsy, so `!sys_admin` is always true and `!developer` is never evaluated.
    if (!sys_admin || !developer) {
      return EMPTY;
    }
    // istanbul ignore next -- unreachable: every path above returns.
    return EMPTY;
  }

  public refreshApiToken$(id: number): Observable<boolean> {
    const { sys_admin, developer } = this.getUserInfo();

    if (id && (sys_admin || developer)) {
      // @ts-ignore
      return this.messages.request$<IAuthResponse>("refreshApiToken", { id }).pipe(
        // the new token is shown once by the caller and never kept in storage
        tap(() => {
          const user = this.getUserInfo();
          const new_user_info = {
            ...user,
            api_token: {
              ...user.api_token,
              status: 1,
            },
          };
          this.setUserStorage(new_user_info as IAuthResponse);
        }),
      );
    }
    if (!sys_admin || !developer) {
      return EMPTY;
    }
    // istanbul ignore else -- unreachable: reaching here needs sys_admin && developer, which
    // (given the first guard did not return) forces `id` to be falsy.
    if (!id) {
      this.notification.error("Operation invalid!");
      return EMPTY;
    }

    // istanbul ignore next -- unreachable: every path above returns.
    return EMPTY;
  }

  public invalidateToken$(id: number): Observable<boolean> {
    const { sys_admin, developer } = this.getUserInfo();

    if (id && (sys_admin || developer)) {
      return this.messages.request$("invalidateApiToken", { id }).pipe(
        tap(() => {
          const user = this.getUserInfo();
          const new_user_info = {
            ...user,
            api_token: {
              ...user.api_token,
              status: 0,
            },
          };
          this.setUserStorage(new_user_info as IAuthResponse);
        }),
      );
    }

    if (!sys_admin || !developer) {
      return EMPTY;
    }

    // istanbul ignore else -- unreachable: reaching here needs sys_admin && developer, which
    // (given the first guard did not return) forces `id` to be falsy.
    if (!id) {
      this.notification.error("Operation invalid!");
      return EMPTY;
    }

    // istanbul ignore next -- unreachable: every path above returns.
    return EMPTY;
  }

  public getApiDocs$(): Observable<any> {
    return this.messages.request$("getApiDocs");
  }

  public createApiToken$(org_id: number, be_id: number, user_id: number, type: string): Observable<boolean> {
    const { sys_admin, developer } = this.getUserInfo();
    if (!org_id) {
      this.notification.error("Operation invalid!");
      return EMPTY;
    }
    if (sys_admin || developer) {
      return this.messages.request$("createDbToken", { org_id, be_id, user_id, type });
    } else {
      return EMPTY;
    }
  }

  public relogin$(): Observable<boolean> {
    if (!this.hasAppToken()) {
      return of(false);
    }
    this.progress.start();
    const bookeng_app_token = this.token.get() as string;
    // @ts-ignore
    return this.messages.request$<IAuthResponse>("reLogin", { bookeng_app_token }).pipe(
      switchMap((data: IAuthResponse) => {
        this.setUserStorage(data);
        this.markPostReloginGracePeriod();
        const effectiveBillingEntityId = this.resolveLoginBillingEntityId(
          data.billing_entity_id,
          data.booking_defaults,
        );
        // @ts-ignore
        return this.messages.request$<{ bookable_entities: IBookableEntity[] }>("getBookableEntities").pipe(
          take(1),
          catchError(() => of({ bookable_entities: [] as IBookableEntity[] })),
          tap(({ bookable_entities }) => {
            const effectiveOrgId = this.resolveOrgIdForBillingEntityFromBookable(
              bookable_entities,
              effectiveBillingEntityId,
              data.org_id,
            );
            this.curr_billing_entity_id = effectiveBillingEntityId;
            this.curr_org_id = effectiveOrgId;
            this.current_org$$.next(effectiveOrgId);
            this.curr_billing_entity_id$$.next(effectiveBillingEntityId);
            this.getKbRecords(effectiveOrgId);
            this.setCachedBillingEntity(effectiveOrgId, effectiveBillingEntityId);
            this.capabilities.changeOrg(effectiveOrgId);
            this.capabilities.changeBE(effectiveBillingEntityId);
            this.capabilities.reloadCapabilities();
            this.getServiceTypes();
            this.getSummaryOptions();
            this.getBookingCreationTypes();
            this.common.getMachineLanguages$().subscribe();
            this.getRegions$().subscribe();
          }),
        );
      }),
      map(() => true),
      catchError(() => {
        this.token.empty();
        return of(false);
      }),
      tap((result) => {
        this.authStatusChange$$.next(result as boolean);
      }),
      tap(() => {
        this.progress.stop();
      }),
    );
  }

  public postSSOLogin$(access_token: string, id_token: string, expires_in: number): Observable<unknown> {
    this.progress.start();
    // @ts-ignore
    return this.messages.request$<ILoginResponse>("postSSOLogin", { access_token, id_token, expires_in }).pipe(
      this.authorize.bind(this),
      catchError((err) => {
        this.error.handleError(err, "@auth.service2");
        if (err instanceof MessageError) {
          const e = err as MessageError;
          // return [500, 419, 417].includes(e.getStatus()) ? from(this.router.navigate(['bookings'])) : EMPTY;
          return EMPTY;
        }
        this.progress.stop();
        return EMPTY;
      }),
      tap(this.progress.stop.bind(this.progress)),
    );
  }

  public getServiceTypes() {
    return this.messages
      .request$("getServiceTypes")
      .pipe(
        tap((data: any) => {
          this.service_types_list = [...(data || [])];
        }),
      )
      .subscribe();
  }

  /** Refreshes cached service types from the server (e.g. after admin CRUD or before billing-entity UI). */
  public refreshServiceTypes$(): Observable<any[]> {
    return this.messages.request$("getServiceTypes").pipe(
      tap((data: any) => {
        this.service_types_list = [...(data || [])];
      }),
      take(1),
    );
  }

  getSummaryOptions = () => {
    return this.messages
      .request$("getSummaryOptions")
      .pipe(
        tap((data: any) => {
          this.summary_option_list = orderBy(data, ["id"], ["asc"]);
        }),
      )
      .subscribe();
  };

  public getBookingCreationTypes = () => {
    return this.messages
      .request$("getBookingCreationTypes")
      .pipe(
        tap((data) => {
          this.booking_creation_types_list = data;
        }),
      )
      .subscribe();
  };

  public getAllServiceTypes() {
    return Array.isArray(this.service_types_list) ? this.service_types_list : [];
  }

  public getBookingCreationTypesList = () => {
    return (this.booking_creation_types_list || [])?.map((i: any) => ({
      ...i,
      value: i.short_id,
      title: getBookingTypeTile(i.short_id),
    }));
  };

  public getAvailableOrgServiceTypes(org_id?: number) {
    const avl_srv_types = this.org_srv_type_list[org_id || this.curr_org_id];
    return this.getAllServiceTypes()?.filter((i: any) => avl_srv_types?.includes(i?.id));
  }

  public getServiceTypesList() {
    return this.getAllServiceTypes()?.map((i: any) => ({ ...i, value: i.srv_id, title: i.name, display_title: serviceTypeDisplayLabel(i.name) }));
  }

  public getSummaryOptionsList = () => {
    return (this.summary_option_list || [])?.map((i: any) => ({ ...i, value: i.option_id, title: i.name }));
  };

  public logout(): void {
    const { user_id, first_name, last_name } = this.getUserInfo();
    this.common
      .setAnalytics$({
        user_id,
        action_type: "logout",
        timestamp: moment().toISOString(),
        details: { message: `User ${first_name} ${last_name} logout.` },
      })
      .subscribe();
    this.token.empty();
    this.user.empty();
    this.logout$$.next(this.cache_user);
    // @todo should be fixed to reload capabilities correctly
    // istanbul ignore next -- not coverable under Karma/Chrome: `Location.reload` is a
    // non-configurable, non-writable own property so it cannot be spied, and letting it run
    // would reload the Karma context iframe and restart the suite.
    document.location.reload();
  }

  public getSignInURL$(email: string): Observable<boolean> {
    const regex = /^.+@(.+)$/g;
    const emailDomain: any[] = regex.exec(email) || [""];
    this.progress.start();
    const params = new HttpParams()
      .set("appClientId", environment.sso.appClientId)
      .set("emailDomain", emailDomain[1] as string);
    this.progress.start();

    return this.http
      .get<{
        signInUrl: "string";
      }>(environment.sso.server + "/v1/identity-providers/sign-in-url", { params })
      .pipe(
        pluck("signInUrl"),
        tap(
          // istanbul ignore next -- not coverable under Karma/Chrome: real Location navigation
          // (even a same-document fragment URL) destabilized the spec-runner iframe in practice,
          // so no test ever lets this callback actually run.
          (signInUrl: string) => {
            document.location.href = signInUrl;
          },
        ),
        mapTo(true),
        catchError((err) => {
          console.info(err);
          this.progress.stop();
          return of(false);
        }),
      );
  }

  public googleLogin$(code: string): Observable<any> {
    this.progress.start();
    // @ts-ignore
    return this.messages.request$<ILoginResponse>("googleLogin", { code }).pipe(
      this.authorize.bind(this),
      catchError((err) => {
        this.progress.stop();
        throw err;
      }),
      tap(this.progress.stop.bind(this.progress)),
    );
  }

  private authorize(source: Observable<ILoginResponse>) {
    return source.pipe(
      switchMap((user) => {
        const {
          bookeng_app_token,
          billing_entity_id,
          email,
          first_name,
          last_name,
          org_id,
          sys_admin,
          ops_group,
          time_zone,
          theme,
          admin_group,
          hide_private_viewer_dropdown,
          hide_mlt,
          read_only_user,
          hide_consumers_manager_view,
          api_token,
          booking_defaults,
          developer,
          allow_summary,
          allow_transcript_tools,
          allow_personal_permanent_link,
          allow_ics_calendar,
          personal_permanent_guid,
          ics_calendar_guid,
        } = user;

        if (!bookeng_app_token) {
          throw new LoginError("No app_token provided");
        }
        this.token.set(bookeng_app_token);

        const new_user = {
          bookeng_app_token,
          allow_summary,
          allow_transcript_tools,
          developer,
          billing_entity_id,
          email,
          first_name,
          last_name,
          org_id,
          sys_admin,
          ops_group,
          time_zone,
          theme,
          admin_group,
          hide_private_viewer_dropdown,
          hide_mlt,
          read_only_user,
          hide_consumers_manager_view,
          api_token,
          booking_defaults,
          allow_personal_permanent_link,
          allow_ics_calendar,
          personal_permanent_guid,
          ics_calendar_guid,
        };

        this.common.getMachineLanguages$().subscribe();
        this.getServiceTypes();
        this.getSummaryOptions();
        this.getBookingCreationTypes();
        this.setUserStorage(new_user);

        const effectiveBillingEntityId = this.resolveLoginBillingEntityId(billing_entity_id, booking_defaults);

        // @ts-ignore
        return this.messages.request$<{ bookable_entities: IBookableEntity[] }>("getBookableEntities").pipe(
          take(1),
          catchError(() => of({ bookable_entities: [] as IBookableEntity[] })),
          tap(({ bookable_entities }) => {
            const effectiveOrgId = this.resolveOrgIdForBillingEntityFromBookable(
              bookable_entities,
              effectiveBillingEntityId,
              org_id,
            );

            this.current_org$$.next(effectiveOrgId);
            this.curr_billing_entity_id = effectiveBillingEntityId;
            this.curr_org_id = effectiveOrgId;
            this.getKbRecords(effectiveOrgId);
            this.curr_billing_entity_id$$.next(effectiveBillingEntityId);

            this.setCachedBillingEntity(effectiveOrgId, effectiveBillingEntityId);
            this.capabilities.changeOrg(effectiveOrgId);
            this.capabilities.changeBE(effectiveBillingEntityId);

            this.cache_user = new_user;
            this.markPostReloginGracePeriod();
            const strStore = localStorage.getItem("bookingsRedirectLoginItem");

            this.common
              .setAnalytics$({
                user_id: user.user_id,
                action_type: "logins",
                timestamp: moment().toISOString(),
                details: { message: `User ${user.first_name} ${user.last_name} login.` },
              })
              .subscribe();

            if (strStore) {
              const item = JSON.parse(strStore);
              if (item) {
                this.url = item.url;
                this.listOfUsers = item.listOfUsers;
              }
              localStorage.removeItem("bookingsRedirectLoginItem");
            }

            if (this.url && this.url.length > 0) {
              this.route = "";
              this.process();
            } else {
              this.router.navigate(["bookings"]);
              setTimeout(() => {
                // window.location.reload()
              }, 1000);
            }
            this.getRegions$().subscribe();
          }),
          tap(() => this.authStatusChange$$.next(true)),
        );
      }),
    );
  }

  setCachedBillingEntity(org_id: number, billing_entity_id?: number, newData?: IBillingEntity) {
    const user = this.getUserInfo();
    const be_id = billing_entity_id || user?.billing_entity_id;

    if (newData) {
      this.cached_billing_entity = newData;
      const new_user = {
        ...user,
        be_booking_defaults: this.cached_billing_entity.booking_defaults,
      };
      this.setUserStorage(new_user);
      this.capabilities.updateBookingTypes(newData);
    } else {
      this.common
        .getBillingEntities$(org_id)
        .pipe(
          take(1),
          tap((data) => {
            const found = data?.find((i: IBillingEntity) => i.billing_entity_id === be_id);

            if (data.length && be_id && found) {
              this.cached_billing_entity = found;
            } else {
              this.cached_billing_entity = data[0];
            }
            const new_user = {
              ...user,
              be_booking_defaults: this.cached_billing_entity.booking_defaults,
            };
            this.setUserStorage(new_user);
            this.capabilities.updateBookingTypes(data[0]);
          }),
        )
        .subscribe();
    }
  }

  setUserStorage(data: IAuthResponse) {
    if (data) {
      this.user.set(data);
      this.logged_in_user = data;
    }
  }

  checkIfUserPresent(): boolean {
    if (this.url == "/knowledge-base") {
      localStorage.removeItem("bookingsRedirectLoginItem");
      return true;
    }

    if (this.listOfUsers) {
      try {
        // Check if the domains instead of a full email address
        const checkDomains = this.listOfUsers.indexOf("@");
        if (checkDomains == -1) {
          const userEmail = this.getUserInfo().email;
          const at_symbol = userEmail.indexOf("@");
          const userDomain = userEmail.substr(at_symbol + 1);
          const arrDomain = this.listOfUsers.split(",");
          if (arrDomain.includes(userDomain)) {
            return true;
          }
        }
        const userEmail = this.getUserInfo().email;
        const arrEmail = this.listOfUsers.split(",");
        if (arrEmail.includes(userEmail)) {
          return true;
        }
      } catch {
        return false;
      }
    }
    return false;
  }

  public checkIfUserAuthenticated(): boolean {
    if (this.getUserInfo() && this.getUserInfo().email) {
      return true;
    }
    return false;
  }

  redirectToLogin() {
    const item: any = { url: this.url, listOfUsers: this.listOfUsers };
    localStorage.setItem("bookingsRedirectLoginItem", JSON.stringify(item));

    this.router.navigate(["/bookings"]);
  }

  redirectToUrl() {
    if (this.url && this.router.url !== this.url) {
      if (this.url.indexOf("?") > -1) {
        const arrUrl = this.url.split("?");
        const url = arrUrl[0];
        const strParams: string = arrUrl[1];
        const params: any = {};
        const arrParams = strParams.split("&");
        for (const iParams of arrParams) {
          const arr = iParams.split("=");
          if (arr && arr.length > 1) {
            params[arr[0]] = arr[1];
          }
        }
        this.router.navigate([url], { queryParams: params });
      } else {
        this.router.navigate([this.url]);
      }
    }
  }

  redirectToNotAuthorized() {
    this.router.navigate(["/notauthorized"]);
  }

  process(): boolean {
    if (this.checkIfUserAuthenticated()) {
      if (this.checkIfUserPresent()) {
        this.redirectToUrl();
        this.url = "";
        this.listOfUsers = "";
        return true;
      } else {
        this.url = "";
        this.listOfUsers = "";
        this.redirectToNotAuthorized();
      }
    } else {
      this.redirectToLogin();
    }
    return false;
  }

  getKbRecords(org_id: number) {
    this.isLoggedIn$
      .pipe(
        tap((isLoggedIn) => {
          if (isLoggedIn) {
            this.messages
              .request$("getKnowledgeBaseRecords", { org_id })
              .pipe(
                tap((data: any) => {
                  this.kb_data$$.next(
                    data.map((i: any) => ({
                      ...i,
                      created_at: moment(i.created_at).format("LLL").trim(),
                      stat: i.status ? "Active" : "Inactive",
                    })),
                  );
                }),
              )
              .subscribe();
          }
        }),
      )
      .subscribe();
  }

  changeOrgId(org_id: number) {
    this.curr_org_id = org_id;
    this.current_org$$.next(org_id);
    this.getKbRecords(org_id);
  }

  getBookingDefaults(data?: IUser) {
    const { booking_defaults, last_name, first_name, email } = data ?? this.getUserInfo();
    const billing_entity = this.cached_billing_entity || {};
    const { booking_defaults: be_booking_defaults } = billing_entity;

    let book_def = {
      notes: "",
      private_link_type: "anyone",
      allow_transcript: 1,
      caption: -1,
      audio: -1,
      audio_caption_details: 1,
      calendar_start_day: 0,
      point_of_contact: String(`${first_name} ${last_name} - ${email}`),
      allow_confirmed_emails: 1,
      allow_canceled_emails: 1,
      allow_received_emails: 1,
      ai_live_caption_setting: {
        font: "Arial",
        size: "44px",
        colourcombination: "C1",
        oneword: "Fast",
      },
    } as IBookingDefaults;

    // billing entity
    if (be_booking_defaults?.calendar_start_day !== undefined) {
      book_def = be_booking_defaults;
    }

    // user
    if (booking_defaults?.calendar_start_day !== undefined) {
      book_def = booking_defaults;
    }

    return book_def;
  }

  getCachedBillingEntity() {
    return this.capabilities.current_be || this.cached_billing_entity;
  }

  getRegions$() {
    return this.messages.request$("getRegions").pipe(
      take(1),
      tap(({ regions }: { regions: IRegion[] }) => {
        this.regions = regions;
      }),
    );
  }

  getBillingEntityRegion = () => {
    if (!this.regions?.length) {
      return null;
    }

    const found = this.regions?.find((r) => r?.region_id === this.cached_billing_entity?.region_id);

    if (found && found.region_name !== "Asia") {
      return found;
    }

    return null;
  };

  getRawBookingSessionLink = (session_id: number) => {
    return this.messages.request$("getRawBookingSessionLink", { session_id });
  };

  /** Path segment from `getRawBookingSessionLink` (Ai-Live viewer URL suffix); null if missing or on error. */
  getBookingSessionLinkValue = async (session_id: number): Promise<string | null> => {
    try {
      const resp = (await this.getRawBookingSessionLink(session_id).toPromise()) as {
        booking_session_link?: { value?: string | null };
      } | null;
      const v = resp?.booking_session_link?.value;
      if (v == null) {
        return null;
      }
      const s = String(v).trim();
      return s === "" ? null : s;
    } catch {
      return null;
    }
  };

  getAiLiveViewerUrl = async (session_id: number) => {
    const { booking_session_link } = await this.getRawBookingSessionLink(session_id).toPromise();
    const { creator_location, value } = booking_session_link || {};

    if (!value) {
      return "";
    }
    return domain_urls[creator_location] ? `${domain_urls[creator_location]}${value}` : `${domain_urls.us}${value}`;
  };
}
